import {
  useRef,
  useContext,
  createContext,
  useCallback,
  useMemo,
  useState,
  useEffect,
  Dispatch,
  SetStateAction,
  RefObject,
  ReactNode,
} from 'react';
import mitt from 'mitt';
import type { Emitter } from 'mitt';
import type { PCMEvent, RawPCMMonoAudioRecorder } from './RawPCMMonoAudioRecorderWorklet';
import {
  NVOQ_PREFERRED_AUDIO_CHUNK_TIME_MS,
  RawPCMMonoAudioRecorder as RawPCMMonoAudioRecorderWorklet,
} from './RawPCMMonoAudioRecorderWorklet';
import { DownsampleRawPCMMonoAudioRecorder as DownsampleRawPCMMonoAudioRecorderWorklet } from './DownsampleRawPCMMonoAudioRecorderWorklet';

import { useToasterDispatch } from 'common/ui/Toaster';
import Text from 'common/ui/Text';
import { useWorklistItemAnalytics } from 'hooks/useWorklistItemAnalytics';
import { useReporterActionReadiness } from 'domains/reporter/Reporter/ReporterActionReadiness';
import { useRecoilCallback, useRecoilValue, useSetRecoilState } from 'recoil';
import { marks, measures } from 'domains/reporter/RichTextEditor/analytics/performance';
import { reporter } from 'modules/analytics/constants';
import analytics from 'modules/analytics';
import { performance } from 'utils/performance';
import { audioContextLazy, DEFAULT_EXPERIMENTAL_CONFIGURATION } from './AudioContextLazy';
import {
  getRecorderReadinessKey,
  hasRecordedForCaseAtom,
  recorderReadinessAtom,
  recorderState,
} from './state';
import { useCurrentCaseId } from 'hooks/useCurrentCase';
import { useMicrophone } from 'domains/reporter/useMicrophone';
import { useFeatureFlagEnabled, FF } from 'modules/feature-flags';
import type { BlobEvent } from 'types';
import { useReporterFeedbackToast } from 'hooks/useReporterFeedbackToast';
import { Editor, Range, RangeRef } from 'slate';
import { SlateMediaRecorder } from './SlateMediaRecorder';
import Deferred from 'utils/deferred';
import { reportStatusState, PROVISIONAL_SUBMIT_STATUSES } from 'domains/reporter/Reporter/state';
import type { ReportStatuses } from 'domains/reporter/Reporter/state';
import useLatest from 'react-use/esm/useLatest';
import usePrevious from 'react-use/esm/usePrevious';
import useUnmount from 'react-use/esm/useUnmount';
import { stringifyRange } from 'domains/reporter/RichTextEditor/utils/stringify';
import { getSelectionForDictation, partialEditor } from 'domains/reporter/RichTextEditor/utils';
import { useOnUnload } from 'hooks/useOnUnload';
import { useShouldSkipWebsocketCreation } from './useShouldSkipWebsocketCreation';
import { useAudioCueServiceSync } from 'hooks/useAudioCueServiceSync';
import { audioCueService } from 'modules/audioCueService';
import { createLockingFunction } from './utils';
import { useMostRecentInput, RICH_TEXT_EDITOR } from 'hooks/useMostRecentInput';
import { logger } from 'modules/logger';
import { useFocusMode } from 'hooks/useFocusMode';
import { useFocusModeRecordingRestart } from './useFocusModeRecordingRestart';
import { checkShouldUseASRPlex } from 'domains/reporter/RichTextEditor/plugins/dictation/utils';
import { useRetryASRPlexVADWebSocket } from 'domains/reporter/RichTextEditor/plugins/dictation/ASRPlex/useRetryASRPlexVADWebSocket';
import type { DownsampleRawPCMMonoAudioRecorder } from './DownsampleRawPCMMonoAudioRecorderWorklet';
import { asrPlexConfiguration } from 'domains/reporter/RichTextEditor/plugins/dictation/ASRPlex/ASRPlexDictation';
import { telemetryContextFor } from 'modules/Apollo/types';
import { useSlate, useSlateSelection } from 'domains/reporter/RichTextEditor/core';
import {
  createRecordingStartMeasurement,
  createRecordingStopMeasurement,
  type RecordingStartMeasurement,
  type RecordingStopMeasurement,
} from './recordingLifecyclePerformance';
import { deviceListKey } from 'utils/mediaDeviceKeys';
import { createCoalescingLock } from 'utils/coalescingLock';
import type { CoalescingLock } from 'utils/coalescingLock';
import { acquireAudioStream } from './acquireAudioStream';
import { assessStream, getAudioTracks, hasOnlyEndedAudioTracks } from './assessStream';
import type { StreamVerdict } from './assessStream';

// https://github.com/twilio/twilio-video.js/issues/149#issuecomment-322545751
const isGetUserMediaSupported = () => {
  return !!navigator.mediaDevices?.getUserMedia;
};

// getSettings() is where the resolved device lives: with `deviceId: { exact: 'default' }` the
// requested id says nothing about which microphone Chrome actually handed over, and that gap is
// the whole failure mode.
const describeAudioTracks = (tracks: MediaStreamTrack[]) =>
  tracks.map((track) => {
    const { deviceId, groupId } = track.getSettings?.() ?? {};
    return { label: track.label, readyState: track.readyState, deviceId, groupId };
  });

export type RecorderEvent =
  | 'data_available'
  | 'stop_recording'
  | 'silence_detected'
  | 'modify_dictation';

export type RecorderDataAvailableEvent = {
  data: Nullable<Blob>;
  id: string;
  selectionRef?: RangeRef | null;
  duration: number;
  frameCount?: number;
  setShouldSkipWebsocketCreation: (arg1: boolean) => void;
  shouldSkipWebsocketCreation: boolean;
  shouldUseASRPlex: boolean;
};

export type RecorderModifyDictationEvent = {
  id: string;
  audioOffsetMs: number;
  localSequence: number;
};

export type PCMRecorderDataAvailableEvent = PCMEvent & {
  setShouldSkipWebsocketCreation: (arg1: boolean) => void;
  shouldSkipWebsocketCreation: boolean;
  shouldUseASRPlex: boolean;
};

export type RecorderStopRecordingEvent = {
  id: string;
  selection?: Range | null;
  duration: number;
  // When ASR Plex owns dictation, the NvoqQueue must ignore this event. Optional so existing
  // emitters (e.g. test drivers) remain valid; absence is treated as `false`.
  shouldUseASRPlex?: boolean;
};

export type RecorderSilenceDetectedEvent = RecorderStopRecordingEvent;

export type RecorderEventPayloadMap = {
  data_available: RecorderDataAvailableEvent | PCMRecorderDataAvailableEvent;
  stop_recording: RecorderStopRecordingEvent;
  silence_detected: RecorderSilenceDetectedEvent;
  modify_dictation: RecorderModifyDictationEvent;
};

export const isPCMRecorderDataAvailableEvent = (
  event: RecorderEventPayloadMap['data_available']
): event is PCMRecorderDataAvailableEvent => 'buffer' in event;

type RecorderEmitter = Emitter<RecorderEventPayloadMap>;

export const recorderEmitter: RecorderEmitter = mitt<
  Record<string, unknown>
>() as unknown as RecorderEmitter;

// nVoq's recommended period of silence before sending a boundary request
export const REQUEST_BOUNDARY_DELAY = 600;

// delay in ms between UI indication of recording stopping, and actual stopping under the hood
// see original implementation/explanation: https://github.com/SironaMedical/services-frontend/pull/3998
const RECORDING_DELAY_MS = 250;

// Module scope outlives the per-case remount of RecorderProvider (it sits under the case-keyed
// SlateContextProvider), so this marks the page session's first stream, i.e. a true cold start.
let hasAcquiredStreamThisSession = false;

export const __resetRecorderSessionForTests = (): void => {
  hasAcquiredStreamThisSession = false;
};

export type RecorderTarget =
  | 'reporter'
  | 'editMacroPreviewTab'
  | 'editMacroEditTab'
  | 'newMacro'
  | 'newFieldText'
  | 'newFieldPicklist'
  | 'templateEditor';

type RecorderContextProps = Readonly<{
  children: React.ReactNode;
}>;

export type RecorderContextBag = Readonly<{
  isRecording: boolean;
  getRecorder: () =>
    | null
    | RawPCMMonoAudioRecorder
    | DownsampleRawPCMMonoAudioRecorder
    | SlateMediaRecorder;
  getAudioContext: () => AudioContext | null;
  getStream: () => null | MediaStream;
  startRecording: () => Promise<void>;
  stopRecording: () => Promise<void>;
  restartRecording: () => Promise<void>;
  toggleRecording: () => void;
  setRecorderTarget: (arg1: ((arg1: RecorderTarget) => RecorderTarget) | RecorderTarget) => void;
  recorderTarget: RecorderTarget;
  userActivityElementRef: RefObject<HTMLDivElement>;
  sendModifyDictationMessage: () => void;
  setTextEditorElement: (el: HTMLDivElement | null) => void;
  setIsRecorderInitialized: Dispatch<SetStateAction<boolean>>;
}>;

export const RecorderContext: React.Context<Nullable<RecorderContextBag>> =
  createContext<Nullable<RecorderContextBag>>(undefined);

const RecorderModifyDictationContext: React.Context<(() => void) | undefined> = createContext<
  (() => void) | undefined
>(undefined);

const getRecorderSelectionRef = (
  recorder: RawPCMMonoAudioRecorder | DownsampleRawPCMMonoAudioRecorder | SlateMediaRecorder | null
): RangeRef | null => {
  if (recorder == null) {
    return null;
  }

  return recorder.initialSelectionRef ?? null;
};

const releaseRangeRef = (rangeRef: Nullable<RangeRef>): void => {
  rangeRef?.unref();
};

const cancelRecordingStopMeasurements = (measurements: Set<RecordingStopMeasurement>): void => {
  [...measurements].forEach((measurement) => measurement.cancel());
};

const RecorderProviderBase = ({ children }: RecorderContextProps): React.ReactElement => {
  const { enqueueToast } = useToasterDispatch();
  const startRecordingLock = useRef<Deferred<void> | null>(null);
  const stopRecordingLock = useRef<Deferred<void> | null>(null);
  const lifecycleMeasurementAttemptRef = useRef(0);
  const recordingStartMeasurementRef = useRef<RecordingStartMeasurement | null>(null);
  const recordingStopMeasurementsRef = useRef(new Set<RecordingStopMeasurement>());
  const analyticsData = useWorklistItemAnalytics({ context: APOLLO_QUERY_CONTEXT });
  const { audioInputDeviceId, audioInputDevices, isLoaded } = useMicrophone({
    context: APOLLO_QUERY_CONTEXT,
  });
  // Serialized so an unchanged device list re-rendering as a fresh array cannot re-trigger
  // acquisition. `null` while the list is still loading, which is not the same as an empty list.
  const audioInputDevicesRevision = useMemo(
    () => (isLoaded ? deviceListKey(audioInputDevices) : null),
    [audioInputDevices, isLoaded]
  );
  const [isRecording, setIsRecording] = useState(false);
  const [isRecorderInitialized, setIsRecorderInitializedState] = useState(false);
  // Mirrored in a ref because acquisition is async: the state commit lands well after the
  // stream is in hand, and the acquisition guard reading the lagging value re-acquired a
  // perfectly good stream on every page load. The ref is the source of truth for the guard;
  // the state still drives readiness and the external setIsRecorderInitialized contract.
  const isRecorderInitializedRef = useRef(false);
  const setIsRecorderInitialized = useCallback<Dispatch<SetStateAction<boolean>>>((value) => {
    const next = typeof value === 'function' ? value(isRecorderInitializedRef.current) : value;
    isRecorderInitializedRef.current = next;
    setIsRecorderInitializedState(next);
  }, []);
  // Using a transaction instead of useRecoilValue because there seems to be a setPendingSelf
  // conflict that cancels out the atom value set, and not seeing the same behavior with useRecoilTransaction.
  const setIsDisplayingRecorderBorder = useRecoilCallback(
    ({ set }) =>
      (value: boolean) => {
        set(recorderState, value);
      },
    []
  );
  const editor = useSlate();
  const editorSelection = useSlateSelection();
  const cleanStreamRef = useRef<MediaStream | null>(null);
  const [recorderTarget, setRecorderTarget] = useState<RecorderTarget>('reporter');
  const audioContextRef = useRef<AudioContext | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  // What the live stream was actually acquired against, so a change of either can be detected.
  const acquiredDeviceIdRef = useRef<string | null>(null);
  const acquiredDevicesRevisionRef = useRef<string | null>(null);
  // Set when a device change lands mid-dictation; consumed by stopRecording.
  const pendingReacquireRef = useRef(false);
  // Acquisition is async and its inputs can change while it is in flight, so it is serialized:
  // overlapping runs would race to write streamRef and leave the loser's tracks unstopped. A ref
  // rather than useMemo, whose cache React is free to discard — correctness depends on identity.
  const acquisitionLockRef = useRef<CoalescingLock | null>(null);
  acquisitionLockRef.current ??= createCoalescingLock();
  const acquisitionLock = acquisitionLockRef.current;
  // Holds the newest acquisition closure, assigned by the effect that owns it. Everything that
  // wants to say "re-acquire now" goes through reacquire(), which reads the current device and
  // device list rather than whatever a caller's own useCallback happened to capture.
  const acquireRef = useRef<(() => Promise<void>) | null>(null);
  const reacquire = useCallback(() => {
    void acquireRef.current?.();
  }, []);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const recorderRef = useRef<
    RawPCMMonoAudioRecorder | DownsampleRawPCMMonoAudioRecorder | SlateMediaRecorder | null
  >(null);
  const vadRecorderRef = useRef<RawPCMMonoAudioRecorder | null>(null);
  const destinationRef = useRef<MediaStreamAudioDestinationNode | null>(null);
  const resolveRecordingDelayRef = useRef<(() => void) | null>(null);
  const [asrPlexFeatureEnabled] = useFeatureFlagEnabled(FF.REPORTER_ASR_PLEX);
  const reportStatus = useRecoilValue<ReportStatuses>(reportStatusState);
  const recordingDelay = RECORDING_DELAY_MS;

  const vadWebsocket = useRetryASRPlexVADWebSocket();
  const silenceDetectedRef = useRef<boolean>(false);
  const silenceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { isMousePressed: mousePressedOnTextEditor, setElement: setTextEditorElement } =
    useMostRecentInput({ id: RICH_TEXT_EDITOR, context: APOLLO_QUERY_CONTEXT });
  const currentCaseId = useCurrentCaseId();
  const previousCaseId = usePrevious(currentCaseId);
  const setHasRecordedForCase = useSetRecoilState(hasRecordedForCaseAtom(currentCaseId ?? ''));

  // Capture the normalized base reporter route once at mount. Normalizing (vs the raw
  // pathname) keeps the atom key stable across in-reporter navigation so the viewer reads
  // the same key the reporter writes. The ref guards against key churn on re-render.
  const reporterPathname = useRef(getRecorderReadinessKey(window.location.pathname)).current;
  const setReadiness = useSetRecoilState(recorderReadinessAtom(reporterPathname));

  const [showRecorderNotReadyNotification, setShowRecorderNotReadyNotification] =
    useState<boolean>(false);

  useReporterFeedbackToast(showRecorderNotReadyNotification, {
    enableNotification: true,
    message: 'Unable to start recording. Refresh the page and try again.',
    icon: 'redWarning',
  });

  const [showMicDisconnectedNotification, setShowMicDisconnectedNotification] =
    useState<boolean>(false);

  // Cross-tab rather than a direct enqueueToast: the radiologist is looking at the viewer
  // window, not at the reporter's record button, so a toast confined to the window owning the
  // recorder would go unseen.
  useReporterFeedbackToast(showMicDisconnectedNotification, {
    enableNotification: true,
    message:
      'Microphone disconnected. Reconnect it, or choose another input in dictation settings, then start recording again.',
    icon: 'redWarning',
  });

  const editorSelectionNestedRangeRef = useRef<RangeRef | null>(null);

  const getRecorder = useCallback(() => {
    return recorderRef.current;
  }, []);

  const {
    userActivityElementRef,
    shouldSkipWebsocketCreationRef,
    setShouldSkipWebsocketCreation,
    sendModifyDictationMessage,
  } = useShouldSkipWebsocketCreation({ isRecording, getRecorder, context: APOLLO_QUERY_CONTEXT });

  const { isFocusModeOn } = useFocusMode({
    context: APOLLO_QUERY_CONTEXT,
  });
  const prevIsFocusModeOn = usePrevious(isFocusModeOn);
  const actionReadiness = useReporterActionReadiness();
  const isInitializationMeasuredRef = useLatest(actionReadiness != null && !isFocusModeOn);
  const hasAcquiredStreamRef = useRef(false);
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const shouldUseASRPlex = checkShouldUseASRPlex({
    isFocusModeOn,
    asrPlexFeatureEnabled,
  });

  useAudioCueServiceSync({ context: APOLLO_QUERY_CONTEXT });

  useEffect(() => {
    if (vadWebsocket == null) return;

    // If we detect 600ms of silence, set silence detected to true
    // We only want to do this once per window of silence
    // vad is short for voice activity detection, so:
    // vad = True: voice detected (silence NOT detected)
    // vad = False: silence detected (voice NOT detected)
    const onMessage = (event: MessageEvent) => {
      const message = event.data;
      if (message === 'False' && !silenceDetectedRef.current) {
        silenceTimerRef.current = setTimeout(() => {
          const recorder = recorderRef.current;
          if (!silenceDetectedRef.current && recorder != null) {
            recorderEmitter.emit('silence_detected', {
              id: recorder.id,
              selection: editorSelectionNestedRangeRef.current?.current,
              duration: recorder.getDurationMillis(),
              shouldUseASRPlex,
            });
          }
          silenceDetectedRef.current = true;
          silenceTimerRef.current = null;
        }, REQUEST_BOUNDARY_DELAY);
      } else if (message === 'True' && silenceDetectedRef.current) {
        const silenceTimer = silenceTimerRef.current;
        if (silenceTimer != null) {
          clearTimeout(silenceTimer);
        }
        silenceDetectedRef.current = false;
        silenceTimerRef.current = null;
      }
    };

    vadWebsocket.addEventListener('message', onMessage);
    return () => {
      vadWebsocket.removeEventListener('message', onMessage);
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
        silenceTimerRef.current = null;
      }
    };
  }, [vadWebsocket, shouldUseASRPlex]);

  // After feature flags have loaded and audio context has initialized, we can start the media stream.
  // The stream is reused for all recordings until something invalidates it: the selected device
  // changes, the audio input device list changes, or the tracks we hold have died.
  useEffect(() => {
    const createMediaStream = async (verdict: StreamVerdict): Promise<MediaStream | undefined> => {
      let ac;

      try {
        ac = await audioContextLazy.get();
        if (audioContextRef.current == null) {
          audioContextRef.current = ac;
        }

        if (recorderRef.current) {
          // Swapping the stream out from under a live recorder would drop the audio it is
          // mid-way through processing, so wait for stopRecording to release it. Device
          // changes can arrive repeatedly during a dictation, hence warn rather than error.
          pendingReacquireRef.current = true;
          logger.warn('[createMediaStream] Deferring stream re-acquisition until recording stops', {
            audioInputDeviceId,
          });
          return;
        }

        const stream = streamRef.current;
        if (stream) {
          logger.info('[createMediaStream] Stream already exists, stopping and removing it', {
            stream: JSON.stringify(stream),
          });
          const tracks: MediaStreamTrack[] = stream.getTracks() ?? [];
          tracks.forEach((track) => track.stop());
          streamRef.current = null;
        }

        const newStream = await acquireAudioStream({
          audioInputDeviceId,
          onNotReady: () => setShowRecorderNotReadyNotification(true),
          onPermissionDenied: () =>
            enqueueToast(
              <>
                Microphone access is required to use dictation. Open Chrome microphone settings to
                enable it.
                <br />
                <code>chrome://settings/content/microphone</code>
                <br />
              </>,
              {
                severity: 'error',
                autoHideDuration: 10000,
                actionName: 'Copy Link',
                actionColor: 'info',
                actionHandler: () => {
                  navigator.clipboard.writeText('chrome://settings/content/microphone');
                },
              }
            ),
        });

        if (newStream == null) {
          logger.warn('[createMediaStream]: No audio stream available', {
            audioContext: JSON.stringify(ac),
            newStream: JSON.stringify(newStream),
          });
          return;
        }

        streamRef.current = newStream;
        acquiredDeviceIdRef.current = audioInputDeviceId;
        acquiredDevicesRevisionRef.current = audioInputDevicesRevision;

        const acquiredTracks = getAudioTracks(newStream);
        // Records which physical device the selected id actually resolved to, which is what
        // makes an intent/reality mismatch visible in collected logs.
        logger.info('[createMediaStream] Acquired audio stream', {
          audioInputDeviceId,
          verdict,
          tracks: describeAudioTracks(acquiredTracks),
        });

        acquiredTracks.forEach((track) => {
          track.onended = () => {
            logger.warn('[createMediaStream] Audio track ended; input device likely removed', {
              audioInputDeviceId,
              tracks: describeAudioTracks([track]),
            });
            // Opportunistic only. `devicechange` is what actually drives recovery, because a
            // replug fires no track event at all; this just shortens the window when `ended`
            // happens to land first.
            setIsRecorderInitialized(false);
          };
        });

        setShowRecorderNotReadyNotification(false);
        return newStream;
      } catch (error: any) {
        logger.error('[createMediaStream]: failed to create media stream', error);
        setShowRecorderNotReadyNotification(true);
      }
    };

    const acquire = async () => {
      // Evaluated per call rather than once per effect run, because reacquire() re-enters here
      // and must re-decide against the current stream instead of replaying an earlier verdict.
      const verdict = assessStream({
        isRecorderInitialized: isRecorderInitializedRef.current,
        acquiredDeviceId: acquiredDeviceIdRef.current,
        audioInputDeviceId,
        acquiredDeviceListKey: acquiredDevicesRevisionRef.current,
        audioInputDeviceListKey: audioInputDevicesRevision,
        tracks: getAudioTracks(streamRef.current),
      });

      if (verdict === 'valid') {
        // Adopt the list we are choosing not to act on, so the next genuine change is still
        // detected even when the acquisition itself predated the list.
        if (audioInputDevicesRevision != null) {
          acquiredDevicesRevisionRef.current = audioInputDevicesRevision;
        }
        return;
      }

      const outcome = await acquisitionLock.run(async () => {
        const startedAt = isInitializationMeasuredRef.current ? performance.now() : null;
        const newStream = await createMediaStream(verdict);
        const endedAt = performance.now();
        // Only mark the recorder initialized once a stream was actually acquired.
        // Otherwise the effect's guard would latch a permanently-null recorder, and record
        // presses would silently no-op until manual mic reselection or reload. Leaving the flag
        // false lets the effect retry when a dep changes (e.g. useMicrophone resetting a stale
        // device id to 'default').
        if (streamRef.current != null) {
          setIsRecorderInitialized(true);
        }

        if (newStream == null) return;
        const reason = !hasAcquiredStreamThisSession
          ? 'startup'
          : !hasAcquiredStreamRef.current
            ? 'case_open'
            : 'reacquire';
        hasAcquiredStreamThisSession = true;
        hasAcquiredStreamRef.current = true;
        if (startedAt == null || !isMountedRef.current || !isInitializationMeasuredRef.current) {
          return;
        }
        performance.measure(reporter.sys.recorderInitializationDuration, {
          start: startedAt,
          end: endedAt,
          detail: { reason, verdict },
        });
      });

      // Newer inputs arrived mid-acquisition — typically the device list resolving just after
      // the stream was requested. Re-run so the guard above is re-evaluated against them,
      // rather than issuing a second getUserMedia against the same device.
      if (outcome === 'drain-requested') {
        reacquire();
      }
    };

    // Assigned here rather than in its own effect: `acquire` closes over exactly this effect's
    // deps, and a separate effect declared later would leave the ref unset for the duration
    // of the first acquisition.
    acquireRef.current = acquire;
    void acquire();

    return () => {
      // Like the former state-counter update, re-acquisition must do nothing after unmount,
      // including when a queued acquisition settles or cleanup stops an active recording.
      acquireRef.current = null;
    };
  }, [
    acquisitionLock,
    audioInputDeviceId,
    audioInputDevicesRevision,
    enqueueToast,
    isInitializationMeasuredRef,
    isRecorderInitialized,
    reacquire,
    setIsRecorderInitialized,
  ]);

  useEffect(() => {
    const hasStream = streamRef.current != null;
    setReadiness(isRecorderInitialized && hasStream);
  }, [isRecorderInitialized, setReadiness]);

  useUnmount(() => {
    setReadiness(false);
  });

  const stopRecording = useCallback(
    async ({ force = false } = {}) => {
      if (force) {
        cancelRecordingStopMeasurements(recordingStopMeasurementsRef.current);
      }

      const activeStopLock = stopRecordingLock.current;
      if (activeStopLock != null) {
        resolveRecordingDelayRef.current?.();
        await activeStopLock.promise;
        return;
      }

      const startLock = startRecordingLock.current;
      const hadPendingStart = startLock?.pending ?? false;
      let stopMeasurement: RecordingStopMeasurement | null = null;
      if (!force) {
        lifecycleMeasurementAttemptRef.current += 1;
        const measurement = createRecordingStopMeasurement({
          attemptId: lifecycleMeasurementAttemptRef.current,
          shouldUseASRPlex,
          hadPendingStart,
          onDispose: () => {
            recordingStopMeasurementsRef.current.delete(measurement);
          },
        });
        stopMeasurement = measurement;
        recordingStopMeasurementsRef.current.add(measurement);
      }

      // A stop supersedes any in-flight start-latency measurement.
      recordingStartMeasurementRef.current?.cancel();

      // Let a pending start commit its state and create the recorder before making the stop
      // visible to the recorder-creation effect. Otherwise an immediate stop can make that
      // effect bail out before any audio recorder exists.
      if (startLock != null) {
        await startLock.promise;
      } else if (isRecording) {
        logger.warn('[useRecorder] startRecordingLock expected to exist in stopRecording callback');
      }

      // Another stop may have claimed the lock while this call was waiting for startup.
      if (stopRecordingLock.current != null) {
        stopMeasurement?.cancel();
        resolveRecordingDelayRef.current?.();
        await stopRecordingLock.current.promise;
        return;
      }

      const stopLock = new Deferred<void>();
      stopRecordingLock.current = stopLock;

      setIsDisplayingRecorderBorder(false);

      audioCueService.playStop();

      if (!force && recordingDelay > 0) {
        // A user's hand action to toggle recording may not be perfectly timed with their speech.
        // By adding a 250ms delay, we will catch any trailing audio that would otherwise be cut off.
        const { promise: recordingDelayPromise, trigger: resolveRecordingDelay } =
          createLockingFunction(recordingDelay);
        resolveRecordingDelayRef.current = resolveRecordingDelay;
        await recordingDelayPromise;
        resolveRecordingDelayRef.current = null;
      }

      const recorder = recorderRef.current;
      const recorderSelectionRef = getRecorderSelectionRef(recorder);
      const vadRecorder = vadRecorderRef.current;

      const resolveStopRecordingLock = () => {
        stopLock.resolve();
        if (stopRecordingLock.current === stopLock) {
          stopRecordingLock.current = null;
        }
      };

      const onStop = () => {
        try {
          stopMeasurement?.measureRecorderStopped();
          performance.mark(marks.sirona.Dictation.RecordingStopped);
          analytics.track(reporter.usr.recordingStopped, {
            selection: editorSelection != null ? stringifyRange(editorSelection) : 'null',
          });
          performance.measure(
            measures.sirona.Dictation.DictationDuration.name,
            measures.sirona.Dictation.DictationDuration.startMark,
            measures.sirona.Dictation.DictationDuration.endMark
          );
          performance.clearMarksByName([
            marks.sirona.Dictation.RecordingStarted,
            marks.sirona.Dictation.RecordingStopped,
          ]);

          // NOTE: This ensures is recording is not set to false until the recorder has done its cleanup
          // otherwise audio frames could be dropped resulting in transcription inaccuracies
          setIsRecording(false);
          stopMeasurement?.scheduleUiPaint();
          resolveStopRecordingLock();
        } finally {
          // The recorder's final data event has completed before this callback runs, so its
          // target can now be detached from the Slate editor without losing final audio.
          releaseRangeRef(recorderSelectionRef);
        }
      };
      if (vadRecorder != null) {
        vadRecorder.stopRecording();
      }
      // Only wait for onStop when the recorder is genuinely still recording — the PCM
      // worklets early-return (and SlateMediaRecorder throws) when already stopped, so
      // onStop would never fire and the stop lock would stay pending forever.
      if (
        recorder != null &&
        ('recording' in recorder ? recorder.recording : recorder.state === 'recording')
      ) {
        try {
          recorder.stopRecording(onStop);
        } catch (error) {
          releaseRangeRef(recorderSelectionRef);
          throw error;
        }
      } else {
        releaseRangeRef(recorderSelectionRef);
        stopMeasurement?.cancel();
        setIsRecording(false);
        resolveStopRecordingLock();
      }

      // Disconnect all the audio nodes in the audio graph
      sourceRef.current?.disconnect();
      sourceRef.current = null;
      recorderRef.current = null;
      vadRecorderRef.current = null;
      destinationRef.current?.disconnect();
      destinationRef.current = null;
      cleanStreamRef.current = null;

      // The recorder has released the stream, so a device change deferred during the dictation
      // can now be honoured. An explicit signal rather than setIsRecorderInitialized(false),
      // which is a no-op when a track's `ended` handler already cleared the flag.
      if (pendingReacquireRef.current) {
        pendingReacquireRef.current = false;
        reacquire();
      }
    },
    [
      isRecording,
      setIsDisplayingRecorderBorder,
      recordingDelay,
      editorSelection,
      shouldUseASRPlex,
      reacquire,
    ]
  );

  // Effect to create new recorder when selection changes
  useEffect(() => {
    if (!isRecording) return;
    const startMeasurement = recordingStartMeasurementRef.current;
    // Only measure the first recorder created for this start attempt. Later effect runs may
    // recreate the recorder after selection or focus changes and are not startup latency.
    const isFirstRecorderForStartAttempt = startMeasurement != null && recorderRef.current == null;
    let didScheduleStartMeasurement = false;
    let dictationSelectionRef: RangeRef | null = null;
    let didTransferDictationSelectionRef = false;

    try {
      if (stopRecordingLock.current != null) {
        return;
      }
      if (previousCaseId !== currentCaseId) {
        stopRecording();
        return;
      }
      // If focus mode is toggled while ASR Plex is enabled, let the restart effect handle stop + start.
      // Otherwise keep the legacy behavior:
      // This is because we need to cleanly teardown and build different pcm and webm recorders.
      // When PCM recorder is used exclusively, we can revisit.
      if (isFocusModeOn !== prevIsFocusModeOn && prevIsFocusModeOn != null) {
        if (asrPlexFeatureEnabled) {
          return;
        }
        stopRecording();
        return;
      }

      // If the mouse is pressed and a recorder was already started, don't create another.
      if (mousePressedOnTextEditor && recorderRef.current != null) {
        return;
      }

      // The dictation selection accounts for differences between the user's native selection
      // and the intended insertion target, such as a collapsed cursor in the middle of a word.
      const selectionForDictation =
        editor != null ? (getSelectionForDictation(editor) ?? editorSelection) : null;

      releaseRangeRef(editorSelectionNestedRangeRef.current);
      editorSelectionNestedRangeRef.current =
        editor != null && editorSelection != null ? Editor.rangeRef(editor, editorSelection) : null;

      // The selection should never be null, this would be a failure mode where the user is
      // dictating and we don't know where to place the dictation in the document.
      if (
        !isFocusModeOn &&
        selectionForDictation == null &&
        !PROVISIONAL_SUBMIT_STATUSES.includes(
          reportStatus as 'provisionalReportSubmit' | 'provisionalAddendumSubmit'
        )
      ) {
        const warningMessage = '[useRecorder] Recording without a selection, skipping';
        logger.warn(warningMessage, {
          reportStatus,
          editor: editor != null ? partialEditor(editor) : 'null',
        });
        return;
      }

      const cleanStream = cleanStreamRef.current;
      if (cleanStream == null) {
        return;
      }

      const currentRecorder = recorderRef.current;
      const currentSelectionRef = getRecorderSelectionRef(currentRecorder);
      if (currentRecorder != null && isFocusModeOn) return;
      if (
        currentRecorder != null &&
        currentSelectionRef?.current != null &&
        selectionForDictation != null &&
        Range.equals(currentSelectionRef.current, selectionForDictation)
      ) {
        // selections have not changed, so do not stop and start a new recorder at the same selection
        return;
      }

      if (shouldSkipWebsocketCreationRef.current === true) {
        return;
      }

      if (currentRecorder != null) {
        const currentSelection = currentSelectionRef?.current ?? null;
        try {
          currentRecorder.stopRecording(() => {
            try {
              recorderEmitter.emit('stop_recording', {
                id: currentRecorder.id,
                selection: currentSelection,
                duration: currentRecorder.getDurationMillis(),
                shouldUseASRPlex,
              });
            } finally {
              releaseRangeRef(currentSelectionRef);
            }
          });
        } catch (error) {
          releaseRangeRef(currentSelectionRef);
          throw error;
        }
      }

      let newRecorder;

      if (shouldUseASRPlex) {
        // Create a PCM recorder when using ASR Plex or when explicitly not using WebM.
        logger.info(`[useRecorder] Creating PCM recorder for ASRPlexDictation`);
        const ac = audioContextRef.current;
        if (ac == null) {
          return;
        }
        if (editor != null && selectionForDictation != null) {
          dictationSelectionRef = Editor.rangeRef(editor, selectionForDictation);
        }
        type RecorderWorkletEvent = Omit<PCMEvent, 'initialSelectionRef'> & {
          initialSelectionRef?: PCMEvent['initialSelectionRef'];
        };
        const initialSelectionRef = dictationSelectionRef ?? undefined;
        const workletOptions = {
          onDataAvailable: (ev: RecorderWorkletEvent) => {
            const normalizedEvent: PCMEvent = {
              ...ev,
              initialSelectionRef: ev.initialSelectionRef ?? null,
            };
            recorderEmitter.emit('data_available', {
              ...normalizedEvent,
              setShouldSkipWebsocketCreation,
              shouldSkipWebsocketCreation: shouldSkipWebsocketCreationRef.current,
              shouldUseASRPlex,
            });
          },
          analytics: analyticsData,
          sourceSampleRate: shouldUseASRPlex
            ? asrPlexConfiguration.audioSamplingRate
            : DEFAULT_EXPERIMENTAL_CONFIGURATION.audioSamplingRate,
          initialSelectionRef,
        } as const;
        if (shouldUseASRPlex) {
          newRecorder = new RawPCMMonoAudioRecorderWorklet(cleanStream, ac, workletOptions);
        } else {
          newRecorder = new DownsampleRawPCMMonoAudioRecorderWorklet(cleanStream, ac, {
            targetSampleRate: 16000,
            ...workletOptions,
          });
        }
        newRecorder.startRecording();
      } else {
        logger.info('[useRecorder] Creating WebM recorder for useNvoqQueue');
        if (editor == null || selectionForDictation == null) {
          logger.warn('[useRecorder] Unable to create SlateMediaRecorder without a selection');
          return;
        }
        dictationSelectionRef = Editor.rangeRef(editor, selectionForDictation);
        newRecorder = new SlateMediaRecorder(cleanStream, {
          mimeType: 'audio/webm',
          initialSelectionRef: dictationSelectionRef,
        });

        newRecorder.addEventListener('dataavailable', (ev: BlobEvent) => {
          recorderEmitter.emit('data_available', {
            data: ev.data,
            id: (ev.target as SlateMediaRecorder).id,
            selectionRef: (ev.target as SlateMediaRecorder).initialSelectionRef,
            duration: (ev.target as SlateMediaRecorder).getDurationMillis(),
            setShouldSkipWebsocketCreation,
            shouldSkipWebsocketCreation: shouldSkipWebsocketCreationRef.current,
            shouldUseASRPlex,
          });
        });
        newRecorder.startRecording(NVOQ_PREFERRED_AUDIO_CHUNK_TIME_MS);
      }
      recorderRef.current = newRecorder;
      didTransferDictationSelectionRef = true;
      if (isFirstRecorderForStartAttempt) {
        startMeasurement.measureRecorderStarted();
        startMeasurement.scheduleUiPaint();
        didScheduleStartMeasurement = true;
      }
    } finally {
      if (!didTransferDictationSelectionRef) {
        releaseRangeRef(dictationSelectionRef);
      }
      if (isFirstRecorderForStartAttempt && !didScheduleStartMeasurement) {
        startMeasurement?.cancel();
      }
    }
  }, [
    isFocusModeOn,
    isRecording,
    reportStatus,
    editorSelection,
    editor,
    shouldUseASRPlex,
    shouldSkipWebsocketCreationRef,
    setShouldSkipWebsocketCreation,
    mousePressedOnTextEditor,
    previousCaseId,
    currentCaseId,
    stopRecording,
    analyticsData,
    prevIsFocusModeOn,
    asrPlexFeatureEnabled,
  ]);

  /* NOTE ON THE AUDIO GRAPH
   * This method bootstraps an audio graph, which looks like:
   *
   * (Mic) MediaStreamSource => (Placeholder) MediaStreamDestination
   *
   * (Placeholder) MediaStreamSource => (Downsampler) AudioWorklet => (Output) MediaDestination
   *
   * We end up creating two disconnected graphs by making a new stream source from the placeholder destination.
   *
   * TODO: This graph can be simpler.
   */
  const startRecording = useCallback(async () => {
    if (startRecordingLock.current?.pending) {
      await startRecordingLock.current.promise;
      return;
    }

    cancelRecordingStopMeasurements(recordingStopMeasurementsRef.current);
    const hadPendingStop = stopRecordingLock.current != null;
    // A start measurement created here persists in the ref until the recorder-creation
    // effect consumes it. If a later startRecording runs before that happens, it reuses
    // the in-flight measurement rather than creating a new one. Only the call that created
    // the measurement "owns" it and may cancel it on its own bail-out paths — a reusing
    // call must leave it alone so the original attempt can still be measured.
    let startMeasurement = recordingStartMeasurementRef.current;
    let ownsStartMeasurement = false;
    if (startMeasurement == null) {
      lifecycleMeasurementAttemptRef.current += 1;
      const measurement = createRecordingStartMeasurement({
        attemptId: lifecycleMeasurementAttemptRef.current,
        shouldUseASRPlex,
        hadPendingStop,
        onDispose: () => {
          if (recordingStartMeasurementRef.current === measurement) {
            recordingStartMeasurementRef.current = null;
          }
        },
      });
      startMeasurement = measurement;
      recordingStartMeasurementRef.current = measurement;
      ownsStartMeasurement = true;
    }

    const cancelOwnedStartMeasurement = () => {
      if (ownsStartMeasurement) {
        startMeasurement.cancel();
      }
    };

    const startLock = new Deferred<void>();
    startRecordingLock.current = startLock;

    // If we are currently stopping, wait for the stop to finish before starting again.
    if (stopRecordingLock.current != null) {
      if (resolveRecordingDelayRef.current != null) {
        resolveRecordingDelayRef.current();
      }
      await stopRecordingLock.current.promise;
    }

    const start = async () => {
      // This is to ensure that a websocket is always created when the user starts a new recording.
      // For context, we previously tried setShouldSkipWebsocketCreation(false) during stopRecording
      // However stableText can still be processed after stopRecording, where setShouldSkipWebsocketCreation(true) may be triggered,
      // and thus preventing new websockets from being created
      setShouldSkipWebsocketCreation(false);

      if (!isGetUserMediaSupported()) {
        cancelOwnedStartMeasurement();
        enqueueToast(
          <>
            This browser isn't supported. Use the{' '}
            <Text
              as="a"
              href="https://www.google.com/chrome/"
              target="_blank"
              rel="noopener noreferrer"
            >
              latest version of Google Chrome
            </Text>{' '}
            to access Sirona Workspace.
          </>,
          { severity: 'error' }
        );
        return;
      }
      if (recorderRef.current) {
        cancelOwnedStartMeasurement();
        // TODO: handle if recorder is already active
        const activeErrorMessage =
          '[useRecorder] Cannot initialize; recorderRef worklet is already initialized';
        logger.error(activeErrorMessage, {
          recorder: JSON.stringify(recorderRef.current),
        });
        return;
      }

      const ac = audioContextRef.current;
      const mediaStream = streamRef.current;

      if (ac == null || mediaStream == null) {
        cancelOwnedStartMeasurement();
        logger.error('[useRecorder] Audio context or media stream is null during startRecording', {
          audioContext: JSON.stringify(ac),
          mediaStream: JSON.stringify(mediaStream),
        });
        return;
      }

      // Correctness backstop, independent of which event told us the device went away. A dead
      // stream still builds a recorder and still emits packets at the normal cadence, so every
      // transcript comes back empty with nothing surfaced anywhere — recording into one has to
      // be refused outright rather than merely logged.
      const audioTracks = getAudioTracks(mediaStream);
      if (hasOnlyEndedAudioTracks(audioTracks)) {
        cancelOwnedStartMeasurement();
        logger.warn('[useRecorder] Refusing to record into a dead media stream; re-acquiring', {
          audioInputDeviceId,
          tracks: describeAudioTracks(audioTracks),
        });
        setShowMicDisconnectedNotification(true);
        setIsRecorderInitialized(false);
        return;
      }

      // Cleared here rather than on successful re-acquisition, which typically completes in
      // well under a second and would close the toast before it could be read. The notice
      // stands until the user actually retries, and clearing re-arms the hook for the next
      // disconnect — it only fires on a false -> true transition.
      setShowMicDisconnectedNotification(false);

      const source = ac.createMediaStreamSource(mediaStream);
      sourceRef.current = source;

      const destination = ac.createMediaStreamDestination();
      destinationRef.current = destination;
      source.connect(destination);

      const cleanStream = destination.stream;
      cleanStreamRef.current = cleanStream;

      // Create a vadRecorder. This isn't being handled in the above effects because
      // we only need to create a single connection per dictation (rather than per selection).
      if (vadRecorderRef.current == null && shouldUseASRPlex === false) {
        const onDataAvailable = ({ data }: PCMEvent) => {
          if (vadWebsocket == null) {
            logger.error(
              '[useRecorder] vadWebsocket is null during onDataAvailable callback',
              vadWebsocket
            );
            return;
          }
          vadWebsocket.send(data);
        };

        const vadRecorder = new RawPCMMonoAudioRecorderWorklet(cleanStream, ac, {
          onDataAvailable,
          sourceSampleRate: asrPlexConfiguration.audioSamplingRate,
          initialSelectionRef: editorSelectionNestedRangeRef.current,
        });
        vadRecorderRef.current = vadRecorder;
        await vadRecorder.onRecorderReady;
        vadRecorder.startRecording();
        vadRecorderRef.current = vadRecorder;
      }

      performance.mark(marks.sirona.Dictation.RecordingStarted);
      audioCueService.playStart();
      analytics.track(reporter.usr.recordingStarted, {
        selection:
          editorSelectionNestedRangeRef.current?.current != null
            ? stringifyRange(editorSelectionNestedRangeRef.current?.current)
            : 'null',
      });
      if (currentCaseId) {
        setHasRecordedForCase(true);
      }

      streamRef.current = mediaStream;

      setIsRecording(true);
      setIsDisplayingRecorderBorder(true);
    };

    try {
      await start();
    } catch (e: any) {
      cancelOwnedStartMeasurement();
      const startErrorMessage = '[useRecorder] Executing start() threw an error';
      logger.error(startErrorMessage, e, {
        selection:
          editorSelectionNestedRangeRef.current?.current != null
            ? stringifyRange(editorSelectionNestedRangeRef.current.current)
            : 'null',
      });
    } finally {
      // Always resolve the lock after start returns or throws.
      startLock.resolve();
      if (startRecordingLock.current !== startLock) {
        const recordingLockError =
          '[useRecorder] Cannot resolve recording lock - startRecordingLock expected to exist in startRecording callback';
        logger.error(recordingLockError);
      }
    }
  }, [
    setShouldSkipWebsocketCreation,
    shouldUseASRPlex,
    currentCaseId,
    setIsDisplayingRecorderBorder,
    enqueueToast,
    vadWebsocket,
    setHasRecordedForCase,
    audioInputDeviceId,
    setIsRecorderInitialized,
  ]);

  const cleanUpRecording = useCallback(() => {
    if (isRecording) {
      stopRecording({ force: true });
    }
  }, [isRecording, stopRecording]);

  useUnmount(() => {
    cleanUpRecording();
  });

  useUnmount(() => {
    releaseRangeRef(editorSelectionNestedRangeRef.current);
    editorSelectionNestedRangeRef.current = null;
  });

  useUnmount(() => {
    recordingStartMeasurementRef.current?.cancel();
    cancelRecordingStopMeasurements(recordingStopMeasurementsRef.current);
  });

  useOnUnload(cleanUpRecording);

  const restartRecording = useCallback(async () => {
    await stopRecording();

    // Wait until stopRecordingLock is done
    if (stopRecordingLock.current?.promise) {
      await stopRecordingLock.current.promise;
    }

    // Wait one tick to let React flush setState (important!)
    await new Promise((r) => setTimeout(r, 0));

    await startRecording();
  }, [stopRecording, startRecording]);

  useFocusModeRecordingRestart({
    isRecording,
    isFocusModeOn,
    prevIsFocusModeOn,
    asrPlexFeatureEnabled,
    stopRecording,
    startRecording,
  });

  const toggleRecording = useCallback(() => {
    if (isRecording && resolveRecordingDelayRef.current == null) {
      stopRecording();
    } else {
      startRecording();
    }
  }, [isRecording, stopRecording, startRecording]);

  // Recoil transactions and Slate selection changes can rerender this provider without changing
  // recorder behavior. Keep the lifecycle methods exposed through context stable so those
  // internal renders do not invalidate every recorder consumer; the wrappers always invoke the
  // latest implementations.
  const startRecordingRef = useLatest(startRecording);
  const stopRecordingRef = useLatest(stopRecording);
  const restartRecordingRef = useLatest(restartRecording);
  const toggleRecordingRef = useLatest(toggleRecording);
  const contextStartRecording = useCallback(() => startRecordingRef.current(), [startRecordingRef]);
  const contextStopRecording = useCallback(() => stopRecordingRef.current(), [stopRecordingRef]);
  const contextRestartRecording = useCallback(
    () => restartRecordingRef.current(),
    [restartRecordingRef]
  );
  const contextToggleRecording = useCallback(
    () => toggleRecordingRef.current(),
    [toggleRecordingRef]
  );

  const getStream = useCallback(() => {
    return streamRef.current;
  }, []);

  const getAudioContext = useCallback(() => {
    return audioContextRef.current;
  }, []);

  const recorderBag = useMemo(
    () => ({
      isRecording,
      startRecording: contextStartRecording,
      recorderTarget,
      setRecorderTarget,
      stopRecording: contextStopRecording,
      restartRecording: contextRestartRecording,
      toggleRecording: contextToggleRecording,
      getRecorder,
      getStream,
      getAudioContext,
      userActivityElementRef,
      sendModifyDictationMessage,
      setTextEditorElement,
      setIsRecorderInitialized,
    }),
    [
      isRecording,
      contextStartRecording,
      recorderTarget,
      setRecorderTarget,
      contextStopRecording,
      contextRestartRecording,
      contextToggleRecording,
      getRecorder,
      getStream,
      getAudioContext,
      userActivityElementRef,
      sendModifyDictationMessage,
      setTextEditorElement,
      setIsRecorderInitialized,
    ]
  );

  return (
    <RecorderModifyDictationContext.Provider value={sendModifyDictationMessage}>
      <RecorderContext.Provider value={recorderBag}>{children}</RecorderContext.Provider>
    </RecorderModifyDictationContext.Provider>
  );
};

export const RecorderProvider = ({ children }: { children: ReactNode }): React.ReactElement => {
  const recorder = useContext(RecorderContext);

  // If a provider already exists up the tree, reuse it so nested providers share the same state.
  if (recorder != null) {
    return <>{children}</>;
  }

  return <RecorderProviderBase>{children}</RecorderProviderBase>;
};

RecorderProvider.displayName = 'RecorderProvider';

export const useRecorder = (): RecorderContextBag => {
  const recorder = useContext(RecorderContext);

  if (!recorder) {
    throw new Error('[useRecorder] Hook must be used within a RecorderProvider.');
  }

  return recorder;
};

export const useSendModifyDictationMessage = (): (() => void) => {
  const sendModifyDictationMessage = useContext(RecorderModifyDictationContext);

  if (sendModifyDictationMessage == null) {
    throw new Error('[useSendModifyDictationMessage] Hook must be used within a RecorderProvider.');
  }

  return sendModifyDictationMessage;
};

const APOLLO_QUERY_CONTEXT = telemetryContextFor(RecorderProvider);
